import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Star, DollarSign, ExternalLink, Copy, CheckCheck, Users, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const TEAL = "#0D9488";

/** Public shareable coach profile — no auth wall.
 *  Coach controls what appears via /coach/profile public_show_* toggles.
 */
export default function PublicCoachPage() {
  const { handle } = useParams();
  const cleanHandle = (handle || "").replace(/^@/, "");
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | not_found
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    setStatus("loading");
    axios.get(`${API_BASE}/api/coach/public/${cleanHandle}`)
      .then(({ data }) => { setData(data); setStatus("ok"); })
      .catch(() => setStatus("not_found"));
  }, [cleanHandle]);

  const copy = (label, value) => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  const shareLink = () => {
    const url = `${window.location.origin}/coach/@${cleanHandle}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied — share it anywhere"));
  };

  if (status === "loading") {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="animate-pulse text-slate-400 text-sm">Loading coach profile…</div>
    </div>;
  }
  if (status === "not_found" || !data) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8" data-testid="public-coach-notfound">
      <div className="text-center max-w-md">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">Coach profile</div>
        <h1 className="font-display font-bold text-2xl text-slate-900 mb-2">This page isn't public</h1>
        <p className="text-sm text-slate-500 mb-6">The coach may have turned off their directory listing or the handle @{cleanHandle} doesn't exist.</p>
        <Link to="/dashboard" className="text-sm font-semibold text-teal-700 hover:text-teal-900">← Back to dashboard</Link>
      </div>
    </div>;
  }

  const v = data.visibility || {};
  const hasContact = v.contact && (data.payment_url || data.payment_venmo || data.payment_paypal);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-coach-page">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <Link to="/dashboard" className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 hover:text-slate-900">
          {data.site_name}
        </Link>
        <button
          type="button"
          onClick={shareLink}
          className="ml-auto text-[11px] uppercase tracking-widest font-semibold text-teal-700 hover:text-teal-900 flex items-center gap-1.5"
          data-testid="public-coach-share"
        >
          <Copy className="w-3 h-3" /> Copy shareable link
        </button>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        {/* Hero cover image */}
        {data.hero_data_url && (
          <div className="mb-6 rounded-2xl overflow-hidden shadow-sm border border-slate-200">
            <img src={data.hero_data_url} alt={`${data.name || data.handle} cover`}
                 className="w-full h-48 md:h-64 object-cover"
                 data-testid="public-coach-hero" />
          </div>
        )}

        {/* Identity block */}
        <div className="flex items-center gap-4">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold shrink-0"
            style={{ background: "linear-gradient(135deg,#5EEAD4 0%,#0D9488 100%)" }}
          >
            {(data.handle || "?").slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-[11px] uppercase tracking-widest font-semibold text-teal-700 flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Verified coach
              </div>
              {data.accepting_clients ? (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-teal-50 text-teal-700">Accepting</span>
              ) : (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-slate-100 text-slate-500">Full</span>
              )}
              {data.avg_rating !== null && (
                <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-amber-600">
                  <Star className="w-3 h-3 fill-current" />
                  {data.avg_rating.toFixed(1)}
                  <span className="text-slate-400 font-normal ml-0.5">({data.rating_count})</span>
                </span>
              )}
            </div>
            <h1 className="font-display font-black text-3xl text-slate-900 mt-1" data-testid="public-coach-name">
              {data.name || `@${data.handle}`}
            </h1>
            <div className="text-[13px] text-slate-500 font-mono-num mt-0.5">@{data.handle} · {data.active_client_count} active client{data.active_client_count === 1 ? "" : "s"}</div>
          </div>
        </div>

        {v.bio && data.coach_bio && (
          <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6" data-testid="public-coach-bio">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" style={{ color: TEAL }} /> About
            </div>
            <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{data.coach_bio}</p>
          </div>
        )}

        {v.specialties && data.coach_specialties?.length > 0 && (
          <div className="mt-4 bg-white border border-slate-200 rounded-xl p-6" data-testid="public-coach-specialties">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-3">Specialties</div>
            <div className="flex flex-wrap gap-1.5">
              {data.coach_specialties.map((s) => (
                <span key={s} className="text-[11px] uppercase tracking-wider px-2.5 py-1 bg-teal-50 text-teal-700 rounded-full border border-teal-100">
                  {s.replace("-", " ")}
                </span>
              ))}
            </div>
          </div>
        )}

        {v.plans && data.payment_plans?.length > 0 && (
          <div className="mt-4 bg-white border border-slate-200 rounded-xl p-6" data-testid="public-coach-plans">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-3 flex items-center gap-1.5">
              <DollarSign className="w-3 h-3" style={{ color: TEAL }} /> Coaching plans
            </div>
            <ul className="space-y-2">
              {data.payment_plans.map((p, i) => (
                <li key={i} className="border border-slate-200 rounded-lg p-3" data-testid={`public-coach-plan-${i}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="text-sm font-semibold text-slate-900">{p.label}</div>
                    <div className="text-lg font-bold text-slate-900 whitespace-nowrap">
                      {p.price}
                      {p.cadence && <span className="text-xs text-slate-500 font-normal ml-1">{p.cadence}</span>}
                    </div>
                  </div>
                  {p.description && <div className="text-[12px] text-slate-500 mt-1 leading-relaxed">{p.description}</div>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {hasContact && (
          <div className="mt-4 bg-white border border-slate-200 rounded-xl p-6" data-testid="public-coach-contact">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-3">How to pay / contact</div>
            {v.contact && data.payment_note && (
              <div className="rounded-lg bg-teal-50 border border-teal-100 p-3 text-[13px] text-teal-900 leading-relaxed mb-3">
                {data.payment_note}
              </div>
            )}
            <div className="space-y-2">
              {data.payment_url && (
                <a href={data.payment_url} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-2 border border-teal-200 rounded-lg px-3 py-2 hover:bg-teal-50 transition-colors text-teal-800"
                   data-testid="public-coach-payment-url">
                  <ExternalLink className="w-4 h-4" />
                  <span className="text-sm font-semibold truncate flex-1">{data.payment_url}</span>
                  <span className="text-[10px] uppercase tracking-widest font-semibold">Open</span>
                </a>
              )}
              {data.payment_venmo && <PayRow label="Venmo" value={data.payment_venmo} onCopy={() => copy("v", data.payment_venmo)} copied={copied === "v"} />}
              {data.payment_paypal && <PayRow label="PayPal" value={data.payment_paypal} onCopy={() => copy("p", data.payment_paypal)} copied={copied === "p"} />}
            </div>
            <div className="text-[10px] text-slate-400 leading-relaxed mt-3">
              {data.site_name} doesn't process these payments. Handle billing directly with the coach.
            </div>
          </div>
        )}

        {v.reviews && data.hall_of_fame?.length > 0 && (
          <div className="mt-4 bg-white border border-slate-200 rounded-xl p-6" data-testid="public-coach-hall-of-fame">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-amber-700 mb-3 flex items-center gap-1.5">
              <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> Hall of fame · 5★ transformations
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {data.hall_of_fame.map((r, i) => (
                <figure key={i} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`public-coach-hof-${i}`}>
                  <img src={r.photo_data_url} alt={`Client @${r.reviewer_handle}`} className="w-full h-32 object-cover" />
                  <figcaption className="p-2.5">
                    <div className="flex items-center gap-1 mb-0.5">
                      {[1,2,3,4,5].map((n) => (
                        <Star key={n} className="w-3 h-3 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono-num">@{r.reviewer_handle}</div>
                    {r.comment && <div className="text-[12px] text-slate-700 leading-snug mt-1 line-clamp-3">{r.comment}</div>}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        )}

        {v.reviews && data.reviews?.length > 0 && (
          <div className="mt-4 bg-white border border-slate-200 rounded-xl p-6" data-testid="public-coach-reviews">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-3 flex items-center gap-1.5">
              <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> Recent reviews
            </div>
            <ul className="space-y-3">
              {data.reviews.map((r, i) => (
                <li key={i} className="border border-slate-100 rounded-lg p-3 bg-slate-50/40">
                  <div className="flex items-center gap-1">
                    <div className="flex items-center gap-0.5">
                      {[1,2,3,4,5].map((n) => (
                        <Star key={n} className={`w-3 h-3 ${n <= r.stars ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
                      ))}
                    </div>
                    <span className="text-[11px] text-slate-500 font-mono-num ml-1">@{r.reviewer_handle}</span>
                    <span className="text-[10px] text-slate-400 ml-auto">{(r.created_at || "").slice(0, 10)}</span>
                  </div>
                  {r.comment && <div className="text-sm text-slate-700 mt-1.5 leading-relaxed">{r.comment}</div>}
                  {r.response && (
                    <div className="mt-2 pl-3 border-l-2 border-teal-300 bg-teal-50/40 rounded-r py-1.5 pr-2">
                      <div className="text-[9px] uppercase tracking-widest font-semibold text-teal-700 mb-0.5">Coach reply</div>
                      <div className="text-[13px] text-slate-800 leading-relaxed">{r.response}</div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-8 flex flex-col sm:flex-row items-center gap-3 border-t border-slate-200 pt-6">
          <Link to="/dashboard" className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 hover:text-slate-900">
            ← Explore more coaches
          </Link>
          <Link
            to="/signup"
            className="sm:ml-auto"
          >
            <Button className="text-white" style={{ background: TEAL }} data-testid="public-coach-cta">
              <Users className="w-3.5 h-3.5 mr-1.5" /> Join {data.site_name} to work with @{data.handle}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function PayRow({ label, value, onCopy, copied }) {
  return (
    <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 shrink-0 w-16">{label}</div>
      <div className="text-sm font-mono-num text-slate-900 truncate flex-1">{value}</div>
      <button type="button" onClick={onCopy}
              className="text-[10px] uppercase tracking-widest font-semibold text-teal-700 hover:text-teal-900 flex items-center gap-1 shrink-0">
        {copied ? <><CheckCheck className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
      </button>
    </div>
  );
}
